import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { ApiConfigService } from './api-config.service'
import { ToastService } from './toast.service'
import { AlertController, Platform } from '@ionic/angular'
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings'
import { TranslateService } from '@ngx-translate/core'

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  private readonly http = inject(HttpClient)
  private readonly apiConfigService = inject(ApiConfigService)
  private readonly toastService = inject(ToastService)
  private readonly alertController = inject(AlertController)
  private readonly platform = inject(Platform)
  private readonly translateService = inject(TranslateService)

  async registerDevice() {
    let permStatus = await PushNotifications.checkPermissions()

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions()
    }

    if (permStatus.receive !== 'granted') {
      this.showSettingsAlert()
    }

    if (permStatus.receive === 'granted') {
      try {
        await PushNotifications.register()
      } catch (e) {
        this.toastService.showToast(JSON.stringify(e))
      }
    }
  }

  async unregisterDevice() {
    await PushNotifications.unregister()
  }

  saveDeviceToken(deviceToken: string) {
    return this.http.post(`${this.apiConfigService.API_NOTIFICATIONS}`, { deviceToken })
  }

  removeDeviceToken(deviceToken: string) {
    return this.http.patch(`${this.apiConfigService.API_NOTIFICATIONS}/${deviceToken}`, {})
  }

  public async showSettingsAlert() {
    const alert = await this.alertController.create({
      header: this.translateService.instant('PUSH_NOTIFICATIONS.ALERT.TITLE'),
      message: this.translateService.instant('PUSH_NOTIFICATIONS.ALERT.MESSAGE'),
      buttons: [
        {
          text: this.translateService.instant('PUSH_NOTIFICATIONS.ALERT.ENABLE'),
          role: 'confirm',
          handler: async () => {
            if (this.platform.is('android')) {
              await NativeSettings.openAndroid({
                option: AndroidSettings.AppNotification,
              })
              try {
                await PushNotifications.register()
              } catch (e) {
                this.toastService.showToast(JSON.stringify(e))
              }
            } else if (this.platform.is('ios')) {
              await NativeSettings.openIOS({
                option: IOSSettings.App,
              })
            }
          },
        },
        {
          text: this.translateService.instant('PUSH_NOTIFICATIONS.ALERT.CANCEL'),
          role: 'cancel',
        },
      ],
    })

    await alert.present()
  }
}
